import { jsx as _jsx } from "react/jsx-runtime";
import { compact } from 'lodash-es';
import { Typography } from 'seagull';
import { isFutureEffectiveDate } from '~/business-logic/billingLogic';
import { BillingSettingsSection } from '~/components/ServiceDetails/BillingSettingsSection/BillingSettingsSection';
import { serviceBillingModel, serviceCostModel, serviceModel, timeModel } from '~/models';
import { RecurrencePeriodTypes } from '~/models/service-billing';
import { formatDateLong } from '~/models/time';
import { formatLongMonthUSDate } from '~/utils';
import { preapprovalPeriodVerb, preapprovalPeriodWordMap, recurrencePeriodWordMap } from '~/utils/billing-period-text';
import { utils } from '..';
import { allStagesHaveSchedule, automaticStagesBullets, bulletPointsTextsPath, legacyInStagesBullet } from './stagesBullets.utils';
export { bulletPointsTextsPath };
function wasAgreementApprovedWithFutureEffectiveDate(params) {
    const { agreementData } = params;
    return agreementData.daysToEffectiveDate > 0;
}
function costDetails(param) {
    let { service, billingTexts, costDisplayTexts } = param;
    const texts = billingTexts.bulletPoints;
    const { cost, billing } = service;
    if (serviceCostModel.isVariablePrice(cost)) {
        if (cost.unitCap === null) {
            return serviceBillingModel.isOnDemand(billing) ? texts.approvalRequired() : null;
        }
        if (cost.unitCap.isZero()) {
            return texts.approvalRequired();
        }
        const unitText = costDisplayTexts.countWithUnit({
            count: cost.unitCap.toString(),
            unit: serviceCostModel.pluralizedUnitName(cost, cost.unitCap.toNumber(), costDisplayTexts)
        });
        if (serviceBillingModel.isRecurring(billing)) {
            const recurrenceText = recurrencePeriodWordMap(billingTexts.periods)[billing.recurrencePeriod];
            return texts.chargingMoreThanRecurring({
                unitText,
                recurrenceText
            });
        }
        if (serviceBillingModel.isOnDemand(billing)) {
            const periodWord = preapprovalPeriodWordMap(billingTexts.periods)[billing.preapprovalPeriod];
            const verb = preapprovalPeriodVerb(billing.preapprovalPeriod, billingTexts.preapprovalVerbs);
            return periodWord ? texts.chargingMoreThanOnDemandWithPeriod({
                unitText,
                periodWord,
                verb
            }) : texts.chargingMoreThanOnDemandNoPeriod({
                unitText
            });
        }
        return texts.chargingMoreThanDefault({
            unitText
        });
    } else if (serviceCostModel.isRangePrice(cost)) {
        const maxPrice = utils.formatDollarAmount(serviceCostModel.applyDiscount(cost));
        return texts.chargingOverMaxPrice({
            maxPrice
        });
    } else {
        return null;
    }
}
function billingSettingsSection(param) {
    let { agreementData, isReadOnly, testId, service, skin, variant, weight, relationshipState, onBillUpfrontModeChange = ()=>{}, onDayOfMonthChange = ()=>{}, onDayOfWeekChange = ()=>{}, onDayOfYearChange = ()=>{} } = param;
    if (!serviceBillingModel.isOngoing(service.billing)) return null;
    return /*#__PURE__*/ _jsx(BillingSettingsSection, {
        service: service,
        testId: testId || '',
        isReadOnly: isReadOnly,
        isActiveAgreement: agreementData.isActiveAgreement(),
        skin: skin,
        variant: variant,
        weight: weight,
        relationshipState: relationshipState,
        onDayOfMonthChange: onDayOfMonthChange,
        onDayOfWeekChange: onDayOfWeekChange,
        onBillUpfrontModeChange: onBillUpfrontModeChange,
        onDayOfYearChange: onDayOfYearChange
    });
}
function getSuffixForAutomaticBilling(params) {
    const texts = params.billingTexts.bulletPoints;
    return isFutureEffectiveDate(params.agreementData.effectiveDate, params.now) || wasAgreementApprovedWithFutureEffectiveDate(params) ? texts.onEffectiveDate() : texts.onProposalAcceptance();
}
function billingDetails(params) {
    const { service, skin, variant, weight, testId, featureFlags } = params;
    const texts = params.billingTexts.bulletPoints;
    const { billing, cost } = service;
    return serviceBillingModel.accept(billing, {
        visitOnApproval: ()=>{
            if (serviceModel.isFromAmendment(service)) return null;
            return texts.billingDoneOnce({
                suffix: getSuffixForAutomaticBilling(params)
            });
        },
        visitOneTimeManual: ()=>texts.canBeBilledOnce(),
        visitOneTimeInStages: (billing)=>{
            const stages = billing.oneTimeInStages.stagesWithFinalAmount(cost);
            const useAutomaticStages = featureFlags.AUTOMATIC_STAGES && allStagesHaveSchedule(stages);
            const stagesBulletParams = {
                texts: params.billingTexts.bulletPoints,
                agreementData: params.agreementData,
                now: params.now
            };
            if (useAutomaticStages) {
                return automaticStagesBullets({
                    stagesWithSchedule: stages,
                    bulletParams: stagesBulletParams
                });
            }
            return legacyInStagesBullet({
                oneTimeInStages: billing.oneTimeInStages,
                stages,
                bulletParams: stagesBulletParams
            });
        },
        visitRepeatableManual: (billing)=>{
            if (serviceCostModel.isVariablePrice(cost)) return null;
            const recurrenceTextMap = {
                [RecurrencePeriodTypes.Weekly]: texts.recurrence.perWeek(),
                [RecurrencePeriodTypes.BiWeekly]: texts.recurrence.everyTwoWeeks(),
                [RecurrencePeriodTypes.Monthly]: texts.recurrence.perMonth(),
                [RecurrencePeriodTypes.Quarterly]: texts.recurrence.perQuarter(),
                [RecurrencePeriodTypes.Yearly]: texts.recurrence.perYear()
            };
            const recurrenceText = recurrenceTextMap[billing.recurrencePeriod];
            return texts.canBeBilledOncePerPeriod({
                recurrenceText
            });
        },
        visitOngoing: ()=>{
            const startBillingAt = serviceModel.startBillingAt(service);
            if (startBillingAt) {
                return /*#__PURE__*/ _jsx(Typography, {
                    noWrap: true,
                    skin: skin,
                    variant: variant,
                    weight: weight,
                    testId: "".concat(testId || '', "-effective-date-message"),
                    children: texts.billingStartsOn({
                        date: formatLongMonthUSDate(startBillingAt)
                    })
                });
            }
            if (serviceModel.isFromAmendment(service)) {
                return null;
            } else if (serviceModel.isBillOnPeriodBeforeNextBilling(service)) {
                return texts.willBeBilled({
                    suffix: getSuffixForAutomaticBilling(params)
                });
            }
            return null;
        },
        visitOnDemand: ()=>null
    });
}
function prorated(param) {
    let { service, billingTexts } = param;
    const texts = billingTexts.bulletPoints;
    const { billing } = service;
    if (serviceBillingModel.isOngoing(billing) && billing.isProrated) return texts.prorated();
    return null;
}
function limitedTimes(param) {
    let { service, billingTexts } = param;
    const texts = billingTexts.bulletPoints;
    const { billing } = service;
    if (!serviceBillingModel.isOngoing(billing) && !serviceBillingModel.isOnDemand(billing)) return null;
    if (!serviceModel.maxChargesEnabled(service)) return null;
    const limit = serviceBillingModel.maxChargesLimit(billing);
    if (limit == null) return null;
    return texts.limitedTimes({
        limit
    });
}
function paused(param) {
    let { service, agreementData, billingTexts } = param;
    const texts = billingTexts.bulletPoints;
    const { billing } = service;
    if (!agreementData.isActiveAgreement() && serviceBillingModel.isOngoing(billing) && billing.isPaused) return texts.paused();
    return null;
}
function priceIncrease(param) {
    let { service, billingTexts } = param;
    const texts = billingTexts.bulletPoints;
    const { cost } = service;
    const increase = serviceCostModel.priceIncrease(cost);
    if (increase.exists()) {
        const increaseOn = increase.increaseOn();
        const suffix = increaseOn ? texts.priceIncreaseStartingOn({
            date: timeModel.formatDateLong(increaseOn)
        }) : texts.priceIncreaseOnAnniversary();
        const increaseBy = increase.isNonPositiveIncrease() ? 'x' : "".concat(increase.percentIncrease(), "%");
        return texts.priceIncrease({
            increaseBy,
            suffix
        });
    }
    return null;
}
function discountPeriodExpiration(params) {
    const { service } = params;
    const texts = params.billingTexts.bulletPoints;
    const discount = serviceModel.discount(service) || serviceModel.expiredDiscount(service);
    if (!discount) return null;
    const discountPeriodExpiration = serviceCostModel.discountPeriodExpiration({
        discount
    });
    if (!discountPeriodExpiration) return null;
    const date = discountPeriodExpiration.date();
    if (date) return texts.discountExpiresOnDate({
        date: formatDateLong(date)
    });
    const periodType = discountPeriodExpiration.period();
    const amount = discountPeriodExpiration.amount();
    if (!periodType || !amount) return null;
    const periodTexts = params.billingTexts.periods;
    const periodCountByType = {
        days: ()=>periodTexts.dayCount({
                count: amount
            }),
        weeks: ()=>periodTexts.weekCount({
                count: amount
            }),
        months: ()=>periodTexts.monthCount({
                count: amount
            }),
        years: ()=>periodTexts.yearCount({
                count: amount
            })
    };
    return texts.discountExpiresAfterEffectiveDate({
        period: periodCountByType[periodType]()
    });
}
export function getServiceBulletPoints(params) {
    const entries = [
        billingSettingsSection(params),
        billingDetails(params),
        costDetails(params),
        prorated(params),
        limitedTimes(params),
        priceIncrease(params),
        discountPeriodExpiration(params)
    ];
    const bullets = entries.flatMap((entry)=>{
        if (entry == null) return [];
        return Array.isArray(entry) ? entry : [
            entry
        ];
    });
    return {
        bullets: compact(bullets),
        pausedCallout: paused(params)
    };
}
